import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import type { Lease, LeaseSignature, Invoice, ApiResponse } from '@/types'
import { customerKeys } from './useCustomers'
import { removeListQueries, invalidateDetailQueries } from '@/lib/queryHelpers'
import { accountSettingsKeys } from './useAccountSettings'
import { invoiceKeys } from './useInvoices'
import { unitKeys } from './useUnits'
import { propertyKeys } from './useProperties'
import { expenseKeys } from './useExpenses'

// Query keys
export const leaseKeys = {
  all: ['leases'] as const,
  lists: () => [...leaseKeys.all, 'list'] as const,
  list: (filters: Record<string, unknown>) => [...leaseKeys.lists(), filters] as const,
  details: () => [...leaseKeys.all, 'detail'] as const,
  detail: (id: number) => [...leaseKeys.details(), id] as const,
}

// Fetch leases
// EP-97: the top-level list is a RENT-ROLL page — it defaults to the LIVE roster
// (active + pending_start + pending_signatures) sorted by unit number asc. Pass
// `status: 'all'` to reveal every lease (incl. draft/ended); `opts.sort` /
// `opts.sort_order` drive the user-selectable server-side column sort.
export function useLeases(
  page = 1,
  status?: string,
  enabled = true,
  q?: string,
  propertyId?: number,
  opts: { sort?: string; sort_order?: string } = {},
) {
  return useQuery({
    queryKey: leaseKeys.list({ page, status, q, propertyId, ...opts }),
    queryFn: async () => {
      const params: Record<string, unknown> = { page }
      if (status) params.status = status
      if (q) params.q = q
      if (propertyId) params.property_id = propertyId
      if (opts.sort) params.sort = opts.sort
      if (opts.sort_order) params.sort_order = opts.sort_order
      const { data } = await api.get<ApiResponse<Lease[]>>('/api/leases', { params })
      return data
    },
    enabled,
  })
}

// Fetch leases for a customer
export function useCustomerLeases(customerId: number) {
  return useQuery({
    queryKey: leaseKeys.list({ customerId }),
    queryFn: async () => {
      const { data } = await api.get<ApiResponse<Lease[]>>(`/api/customers/${customerId}/leases`)
      return data.data
    },
    enabled: !!customerId,
  })
}

// Fetch single lease
export function useLease(id: number, enabled: boolean = true) {
  return useQuery({
    queryKey: leaseKeys.detail(id),
    queryFn: async () => {
      const { data } = await api.get<ApiResponse<Lease>>(`/api/leases/${id}`)
      return data.data
    },
    enabled: !!id && enabled,
  })
}

// Create lease
interface CreateLeaseInput {
  unit_id: number
  customer_id: number
  document_template_id?: number
  started_at: string
  ended_at?: string
  /**
   * T156-011: null lets the server's DepositEngine fill the property
   * default (blank-only fill — an explicit 0 is kept as a genuine waiver).
   */
  deposit: number | null
  /**
   * Skip the draft → pending_signatures → pending_start lifecycle and
   * create the lease directly in `active`. Used by the onboarding wizard's
   * bulk-import (Step 5), which represents already-existing tenancies.
   * The controller permits `:status` and the Lease model snapshot fires on
   * activating_now? when new_record? && status == 'active'.
   */
  status?: 'draft' | 'pending_signatures' | 'pending_start' | 'active' | 'ended'
  // T156-010 (EP-156 §3.3) — two optional, invoicing-independent lease
  // charges. null/undefined = not set, no default.
  move_in_fee_amount?: number | null
  keycard_deposit_amount?: number | null
}

export function useCreateLease() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ unit_id, ...rest }: CreateLeaseInput) => {
      const { data } = await api.post<ApiResponse<Lease>>(`/api/units/${unit_id}/leases`, {
        lease: {
          customer_id: rest.customer_id,
          started_at: rest.started_at,
          ended_at: rest.ended_at,
          deposit: rest.deposit,
          document_template_id: rest.document_template_id,
          move_in_fee_amount: rest.move_in_fee_amount,
          keycard_deposit_amount: rest.keycard_deposit_amount,
          // Forward status when explicitly provided. Without this the
          // server falls through to the model's DB default (`draft`) and
          // the wizard's `status: 'active'` payload silently disappears.
          ...(rest.status ? { status: rest.status } : {}),
        },
      })
      return data.data
    },
    onSuccess: (data) => {
      removeListQueries(queryClient, leaseKeys.lists())
      invalidateDetailQueries(queryClient, customerKeys.detail(data.customer_id))
    },
  })
}

// Update lease
interface UpdateLeaseInput {
  id: number
  started_at?: string
  ended_at?: string
  deposit?: number
  signed_at?: string
  pdf_key?: string
  status?: string
  image_urls?: string[]
  attachment_urls?: string[]
  // EP-136 T136-024 — extending a booking's hold. Server-side this is
  // admin+ only and only valid on a booking (LeasePolicy#extend_reservation_
  // expiry?), guarded separately from the rest of #update; the typed 422s
  // (not_a_booking, expiry_in_past) are forwarded untranslated, callers
  // translate via translateApiError.
  reservation_expires_at?: string
  // T156-010 (EP-156 §3.3) — two optional, invoicing-independent lease
  // charges. null clears the field; undefined leaves it unchanged.
  move_in_fee_amount?: number | null
  keycard_deposit_amount?: number | null
}

export function useUpdateLease() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, ...input }: UpdateLeaseInput) => {
      const { data } = await api.patch<ApiResponse<Lease>>(`/api/leases/${id}`, {
        lease: input,
      })
      return data.data
    },
    onSuccess: (data) => {
      removeListQueries(queryClient, leaseKeys.lists())
      invalidateDetailQueries(queryClient, leaseKeys.detail(data.id))
      invalidateDetailQueries(queryClient, customerKeys.detail(data.customer_id))
    },
  })
}

// Generate sign links
interface GenerateSignLinksInput {
  leaseId: number
  signers: Array<{ role: string; name: string }>
}

export function useGenerateSignLinks() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ leaseId, signers }: GenerateSignLinksInput) => {
      const { data } = await api.post<ApiResponse<LeaseSignature[]>>(
        `/api/leases/${leaseId}/generate_sign_links`,
        { signers }
      )
      return data.data
    },
    onSuccess: (_, { leaseId }) => {
      invalidateDetailQueries(queryClient, leaseKeys.detail(leaseId))
      invalidateDetailQueries(queryClient, [...leaseKeys.details(), leaseId, 'signatures'])
    },
  })
}

// Regenerate a single sign link
interface RegenerateSignLinkInput {
  leaseId: number
  signatureId: number
  signer_name?: string
}

export function useRegenerateSignLink() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ leaseId, signatureId, ...params }: RegenerateSignLinkInput) => {
      const { data } = await api.patch<ApiResponse<LeaseSignature>>(
        `/api/leases/${leaseId}/signatures/${signatureId}`,
        params
      )
      return data.data
    },
    onSuccess: (_, { leaseId }) => {
      invalidateDetailQueries(queryClient, [...leaseKeys.details(), leaseId, 'signatures'])
    },
  })
}

// Get signatures for a lease
export function useLeaseSignatures(leaseId: number) {
  return useQuery({
    queryKey: [...leaseKeys.details(), leaseId, 'signatures'],
    queryFn: async () => {
      const { data } = await api.get<ApiResponse<LeaseSignature[]>>(
        `/api/leases/${leaseId}/signatures`
      )
      return data.data
    },
    enabled: !!leaseId,
  })
}

// EP-130 (T130-011) — the "Bill deposit" convenience: creates a one-line
// deposit invoice for the lease. 409 when one already exists in the
// renewal chain, 422 when the lease has no deposit to bill (condo /
// zero-deposit); the caller translates and toasts those.
export function useCreateDepositInvoice() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (leaseId: number) => {
      const { data } = await api.post<ApiResponse<Invoice>>(`/api/leases/${leaseId}/deposit_invoice`)
      return data.data
    },
    onSuccess: (_, leaseId) => {
      invalidateDetailQueries(queryClient, leaseKeys.detail(leaseId))
      removeListQueries(queryClient, invoiceKeys.lists())
    },
  })
}

// EP-149 T149-079 (MON-7 C): records a deposit held from before FlowRent
// directly on the lease: amount and date, no invoice, no payment. Reverses
// EP-130's original "no off-book deposit" ruling for money that predates
// FlowRent specifically (owner ruling 2026-09-23). date is YYYY-MM-DD.
interface RecordDepositHeldInput {
  leaseId: number
  amount: number
  date: string
}

export function useRecordDepositHeld() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ leaseId, amount, date }: RecordDepositHeldInput) => {
      const { data } = await api.post<ApiResponse<Lease>>(`/api/leases/${leaseId}/deposit_held`, {
        amount,
        date,
      })
      return data.data
    },
    onSuccess: (lease) => {
      invalidateDetailQueries(queryClient, leaseKeys.detail(lease.id))
      removeListQueries(queryClient, leaseKeys.lists())
      // EP-149 T149-081 (MON-7 D): the account-wide deposits-held total on
      // the dashboard changes too.
      queryClient.invalidateQueries({ queryKey: ['dashboard'] })
    },
  })
}

// EP-136 (T136-021/T136-018) — resolves a booked (draft) lease that never
// converts: POST /api/leases/:id/cancel_booking. disposition ('forfeit' |
// 'refund') is optional — LeaseCancelBookingService defaults it to the
// property's resolved booking-fee disposition when omitted. Ends the lease
// and either settles the paid booking fee (forfeit JE / deposit_return
// Expense) or destroys the unpaid booking invoice outright, and releases
// the unit back to bookable.
//
// 422 typed error.code values (not_a_booking, already_ended,
// disposition_invalid) are forwarded untranslated — callers translate via
// translateApiError.
interface CancelBookingInput {
  leaseId: number
  disposition?: 'forfeit' | 'refund'
}

export function useCancelBooking() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ leaseId, ...params }: CancelBookingInput) => {
      const { data } = await api.post<ApiResponse<Lease>>(
        `/api/leases/${leaseId}/cancel_booking`,
        params
      )
      return data.data
    },
    onSuccess: (lease, { leaseId }) => {
      invalidateDetailQueries(queryClient, leaseKeys.detail(leaseId))
      removeListQueries(queryClient, leaseKeys.lists())
      // A settled booking either destroys the unpaid booking invoice or
      // updates it via a settlement, and it always releases the unit back to
      // bookable — same "invalidate the parent's embedded prefix too"
      // convention as useCreateBooking (Properties#show / Units#show embed
      // status/current_booking/bookable).
      removeListQueries(queryClient, invoiceKeys.lists())
      removeListQueries(queryClient, unitKeys.lists())
      removeListQueries(queryClient, unitKeys.propertyLists())
      invalidateDetailQueries(queryClient, unitKeys.detail(lease.unit_id))
      invalidateDetailQueries(queryClient, propertyKeys.details())
    },
  })
}

// EP-156 (T156-009/T156-010, §3.3/A6) — POST /api/leases/:id/end. Ends the
// lease (defaults ended_at to today in the account's own timezone when
// omitted) and, only when the lease has no renewal and carries ค่าแรกเข้า
// and/or a key-card deposit, creates exactly one pending_confirmation
// move_in_refund Expense for their sum. refund_expense is null whenever
// none was created (renewed lease, repeat call, or neither amount set) —
// the caller's toast/link follows this response field, never a guess made
// before the call.
interface EndLeaseInput {
  leaseId: number
  ended_at?: string
}

interface EndLeaseResult {
  lease: Lease
  refund_expense: { id: number; amount: number } | null
}

export function useEndLease() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ leaseId, ended_at }: EndLeaseInput): Promise<EndLeaseResult> => {
      const { data } = await api.post<{ data: Lease; refund_expense: { id: number; amount: number } | null }>(
        `/api/leases/${leaseId}/end`,
        ended_at ? { ended_at } : {}
      )
      return { lease: data.data, refund_expense: data.refund_expense }
    },
    onSuccess: ({ lease, refund_expense }) => {
      removeListQueries(queryClient, leaseKeys.lists())
      invalidateDetailQueries(queryClient, leaseKeys.detail(lease.id))
      invalidateDetailQueries(queryClient, customerKeys.detail(lease.customer_id))
      if (refund_expense) {
        removeListQueries(queryClient, expenseKeys.lists())
        queryClient.invalidateQueries({ queryKey: ['dashboard'] })
      }
    },
  })
}

// EP-162 item 11 follow-up 1: POST /api/leases/:id/renew. Creates a draft
// renewal (renewed_from_id = the source) and returns it; 422 when the server
// refuses (lease ended through End lease, more than 90 days past its end, or
// already renewed).
export function useRenewLease() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (leaseId: number): Promise<Lease> => {
      const { data } = await api.post<{ data: Lease }>(`/api/leases/${leaseId}/renew`, {})
      return data.data
    },
    onSuccess: (renewal, sourceLeaseId) => {
      removeListQueries(queryClient, leaseKeys.lists())
      invalidateDetailQueries(queryClient, leaseKeys.detail(sourceLeaseId))
      invalidateDetailQueries(queryClient, customerKeys.detail(renewal.customer_id))
      invalidateDetailQueries(queryClient, unitKeys.detail(renewal.unit_id))
      queryClient.invalidateQueries({ queryKey: ['dashboard'] })
    },
  })
}

// Delete lease
export function useDeleteLease() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`/api/leases/${id}`)
      return id
    },
    onSuccess: () => {
      removeListQueries(queryClient, leaseKeys.lists())
      // Refresh per-entity counts so empty-state seed/affordance gating is accurate.
      queryClient.invalidateQueries({ queryKey: accountSettingsKeys.all })
    },
  })
}


